import React, { FC, useCallback, useMemo } from 'react';
import { ChakraProvider } from '@chakra-ui/provider';
import { Box, Heading, Text, extendTheme } from '@chakra-ui/react';
import { useTheme } from 'ts/client/gallery';
import { LoadingIndicator } from 'ts/common/components';
import Button from 'ts/common/components/gallery/Button';
import StudioHeading from 'ts/common/components/gallery/StudioHeading';
import StudioLogo from 'ts/common/components/gallery/StudioLogo';

interface GooglePhotosExport {
    clientEmail: string;
    error?: string;
    exportId: number;
    googlePhotosUrl: string | null;
    /** How many of the export's items are each media type, so the copy can name exactly what it holds */
    photoCount?: number;
    videoCount?: number;
    slideshowCount?: number;
}

interface Props {
    brandName: string;
    googlePhotosExport?: GooglePhotosExport;
    spEventBus: SpAngularJs.ISpEventBus;
    translateFilter: SpAngularJs.ITranslateFilter;
}

const GooglePhotosExportPage: FC<Props> = ({
    brandName,
    googlePhotosExport,
    spEventBus,
    translateFilter: t
}) => {
    const { eventTheme, theme } = useTheme(spEventBus);

    const chakraFallbackTheme = useMemo(() => extendTheme({}), []);
    const chakraProviderTheme = theme ?? chakraFallbackTheme;

    const handleCtaButtonClick = useCallback(() => {
        location.href = googlePhotosExport?.googlePhotosUrl || 'https://photos.google.com/';
    }, [googlePhotosExport]);

    /**
     * Picks the copy variant naming exactly which media types this export holds - e.g. `*Video`
     * for one video alone, `*PhotoSlideshow` for photos plus slideshows, `*All` for all three.
     * Falls back to the plain default when no counts are present at all.
     */
    function exportKey(name: string) {
        const { photoCount = 0, videoCount = 0, slideshowCount = 0 } = googlePhotosExport ?? {};
        const hasPhotos = photoCount > 0;
        const hasVideos = videoCount > 0;
        const hasSlideshows = slideshowCount > 0;
        const typeCount = [hasPhotos, hasVideos, hasSlideshows].filter(Boolean).length;

        if (typeCount === 3) {
            return `client.gallery.googlePhotosExport.${name}All`;
        }

        if (typeCount === 2) {
            if (hasPhotos && hasVideos) {
                return `client.gallery.googlePhotosExport.${name}Mixed`;
            }

            if (hasPhotos) {
                return `client.gallery.googlePhotosExport.${name}PhotoSlideshow`;
            }

            return `client.gallery.googlePhotosExport.${name}VideoSlideshow`;
        }

        if (hasVideos) {
            return `client.gallery.googlePhotosExport.${name}${
                videoCount > 1 ? 'Videos' : 'Video'
            }`;
        }

        if (hasSlideshows) {
            return `client.gallery.googlePhotosExport.${name}${
                slideshowCount > 1 ? 'Slideshows' : 'Slideshow'
            }`;
        }

        return `client.gallery.googlePhotosExport.${name}`;
    }

    function getResultHeaderKey() {
        if (googlePhotosExport?.error) {
            return 'client.gallery.googlePhotosExport.error.header';
        }

        return exportKey('ready.header');
    }

    function getResultSubtitleKey() {
        if (googlePhotosExport?.error) {
            return `client.gallery.googlePhotosExport.error.subtitle.${googlePhotosExport.error}`;
        }

        return exportKey('ready.subtitle');
    }

    return (
        <ChakraProvider theme={chakraProviderTheme} resetCSS={false}>
            <Box paddingX="24px" textAlign="center">
                <Box marginBottom="32px" marginTop="16px" textAlign="center">
                    {eventTheme?.eventBrandLogo ? (
                        <StudioLogo
                            logo={eventTheme.eventBrandLogo}
                            logoAltText={eventTheme.logoAltText}
                            logoSize="md"
                        />
                    ) : (
                        <StudioHeading text={brandName} variant="primary" />
                    )}
                </Box>
                {googlePhotosExport ? (
                    googlePhotosExport.googlePhotosUrl || googlePhotosExport.error ? (
                        <>
                            <Heading as="h3" fontWeight="semibold" marginBottom="32px" size="lg">
                                {t(getResultHeaderKey())}
                            </Heading>
                            <Text
                                fontSize="md"
                                margin="0 auto 32px"
                                maxWidth="100%"
                                textAlign="center"
                                width="600px"
                            >
                                {t(getResultSubtitleKey())}
                            </Text>
                            <Button
                                maxWidth="100%"
                                onClick={handleCtaButtonClick}
                                text={t('client.gallery.googlePhotosExport.ctaButton')}
                                width="358px"
                            />
                        </>
                    ) : (
                        <>
                            <LoadingIndicator />
                            <Heading as="h3" fontWeight="semibold" marginBottom="32px" size="lg">
                                {t(exportKey('pending.header'))}
                            </Heading>
                            <Text
                                fontSize="md"
                                margin="0 auto 32px"
                                maxWidth="100%"
                                textAlign="center"
                                width="600px"
                            >
                                {t('client.gallery.googlePhotosExport.pending.text', {
                                    clientEmail: googlePhotosExport.clientEmail
                                })}
                            </Text>
                        </>
                    )
                ) : (
                    <LoadingIndicator />
                )}
            </Box>
        </ChakraProvider>
    );
};

export default GooglePhotosExportPage;
